import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { MoonIcon } from '@solar-icons/react/linear/moon'
import { SunIcon } from '@solar-icons/react/linear/sun'
import { AddIcon } from '@solar-icons/react/linear/add'
import { MagnifierIcon } from '@solar-icons/react/linear/magnifier'
import { StarsIcon } from '@solar-icons/react/bold/stars'
import { CloseIcon } from '@solar-icons/react/linear/close'
import { SidebarMinimalisticIcon } from '@solar-icons/react/linear/sidebar-minimalistic'
import { BookmarkIcon } from '@solar-icons/react/linear/bookmark'
import { BookmarkIcon as BookmarkFilledIcon } from '@solar-icons/react/bold/bookmark'
import { UserCircleIcon } from '@solar-icons/react/linear/user-circle'
import { SettingsIcon } from '@solar-icons/react/linear/settings'
import { LogoutIcon } from '@solar-icons/react/linear/logout'
import { ROUTES } from '../routes'
import { ConnectionIndicator } from './ConnectionIndicator'
import { cn } from '@/lib/utils/cn'
import { useSessionStore } from '@/stores/sessionStore'
import { useChatsStore } from '@/stores/chatsStore'
import type { StoredChat } from '@/stores/chatsStore'
import { useSavedStore } from '@/stores/savedStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useRunQuery } from '@/lib/hooks/useRunQuery'

export interface SidebarProps {
  /** Controls the off-canvas drawer below the lg breakpoint. */
  open: boolean
  onClose: () => void
}

function startOfDay(ms: number): number {
  const date = new Date(ms)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

interface ChatRow {
  chat: StoredChat
  title: string
}

interface RecentGroup {
  label: string
  entries: ChatRow[]
}

/** Buckets chats into Today / Yesterday / Earlier, most recent first. */
function groupRecent(rows: ChatRow[]): RecentGroup[] {
  const today = startOfDay(Date.now())
  const yesterday = today - 86_400_000

  const buckets: Record<'today' | 'yesterday' | 'earlier', ChatRow[]> = {
    today: [],
    yesterday: [],
    earlier: [],
  }

  for (const row of rows) {
    const day = startOfDay(row.chat.updatedAt)
    const bucket = day === today ? 'today' : day === yesterday ? 'yesterday' : 'earlier'
    buckets[bucket].push(row)
  }

  return [
    { label: 'Today', entries: buckets.today },
    { label: 'Yesterday', entries: buckets.yesterday },
    { label: 'Earlier', entries: buckets.earlier },
  ].filter((group) => group.entries.length > 0)
}

function chatTitle(chat: StoredChat): string {
  const first = chat.exchanges[0]?.query.trim().replace(/\s+/g, ' ') ?? 'New chat'
  return first.length > 60 ? `${first.slice(0, 57)}…` : first
}

/** Deterministic, cheerful accent for a saved query's initial-badge. */
const SAVED_BADGE_TONES = [
  'bg-blue-500/12 text-blue-600 dark:bg-blue-400/15 dark:text-blue-300',
  'bg-amber-500/12 text-amber-600 dark:bg-amber-400/15 dark:text-amber-300',
  'bg-teal-500/12 text-teal-600 dark:bg-teal-400/15 dark:text-teal-300',
  'bg-violet-500/12 text-violet-600 dark:bg-violet-400/15 dark:text-violet-300',
  'bg-rose-500/12 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300',
]

function IconRail({
  onNavigate,
  collapsed,
  onToggleCollapse,
}: {
  onNavigate: () => void
  collapsed: boolean
  onToggleCollapse: () => void
}) {
  const navigate = useNavigate()
  const theme = useSettingsStore((state) => state.theme)
  const setTheme = useSettingsStore((state) => state.setTheme)

  const [profileOpen, setProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)

  useEffect(() => {
    if (!profileOpen) return

    const onPointerDown = (event: PointerEvent) => {
      if (!profileRef.current?.contains(event.target as Node)) setProfileOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProfileOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [profileOpen])

  return (
    <div className="flex w-16 shrink-0 flex-col items-center border-r border-line bg-surface py-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-fg shadow-sm">
        <StarsIcon size={16} />
      </span>

      {collapsed && (
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Show sidebar"
          aria-label="Show sidebar"
          className="mt-3 hidden h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover hover:text-fg lg:flex"
        >
          <SidebarMinimalisticIcon size={17} strokeWidth={1.75} className="-scale-x-100" />
        </button>
      )}

      <nav className={cn('flex flex-col items-center gap-1', collapsed ? 'mt-4' : 'mt-5')}>
        {ROUTES.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            end={path === '/'}
            onClick={onNavigate}
            title={label}
            aria-label={label}
            className={({ isActive }) =>
              cn(
                'flex h-10 w-10 items-center justify-center rounded-2xl transition-colors',
                isActive
                  ? 'bg-sunken text-fg'
                  : 'text-subtle hover:bg-hover hover:text-fg',
              )
            }
          >
            <Icon size={19} strokeWidth={1.75} />
          </NavLink>
        ))}
      </nav>

      <div className="flex-1" />

      <div className="flex flex-col items-center gap-1">
        <ConnectionIndicator compact />
        <button
          type="button"
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover hover:text-fg"
        >
          {isDark ? <MoonIcon size={17} strokeWidth={1.75} /> : <SunIcon size={17} strokeWidth={1.75} />}
        </button>

        <div ref={profileRef} className="relative">
          <button
            type="button"
            onClick={() => setProfileOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            title="Profile"
            aria-label="Profile"
            className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover hover:text-fg"
          >
            <UserCircleIcon size={19} strokeWidth={1.75} />
          </button>

          {profileOpen && (
            <div
              role="menu"
              className="animate-rise absolute bottom-0 left-full z-20 ml-2 w-48 overflow-hidden rounded-xl border border-line bg-surface py-1.5 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setProfileOpen(false)
                  navigate('/settings')
                  onNavigate()
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover"
              >
                <SettingsIcon size={15} strokeWidth={1.75} />
                Preferences
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => setProfileOpen(false)}
                title="Coming soon"
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover"
              >
                <UserCircleIcon size={15} strokeWidth={1.75} />
                Account
              </button>

              <div className="my-1.5 h-px bg-line" />

              <button
                type="button"
                role="menuitem"
                onClick={() => setProfileOpen(false)}
                title="Coming soon"
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-danger transition-colors hover:bg-danger-soft"
              >
                <LogoutIcon size={15} strokeWidth={1.75} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate()
  const newSession = useSessionStore((state) => state.newSession)
  const loadSession = useSessionStore((state) => state.loadSession)
  const activeConversationId = useSessionStore((state) => state.conversationId)

  const chats = useChatsStore((state) => state.chats)
  const togglePin = useChatsStore((state) => state.togglePin)
  const savedItems = useSavedStore((state) => state.items)
  const markSavedUsed = useSavedStore((state) => state.markUsed)
  const { run } = useRunQuery()

  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState(false)

  const filteredChats = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return chats
    return chats.filter((chat) =>
      chat.exchanges.some((exchange) => exchange.query.toLowerCase().includes(needle)),
    )
  }, [chats, search])

  const pinnedChats = useMemo(
    () =>
      filteredChats
        .filter((chat) => chat.pinned)
        .map((chat) => ({ chat, title: chatTitle(chat) }))
        .slice(0, 20),
    [filteredChats],
  )

  const groups = useMemo(
    () =>
      groupRecent(
        filteredChats
          .filter((chat) => !chat.pinned)
          .map((chat) => ({ chat, title: chatTitle(chat) }))
          .slice(0, 20),
      ),
    [filteredChats],
  )

  const visibleSaved = useMemo(() => savedItems.slice(0, 4), [savedItems])

  const openChat = (chat: StoredChat) => {
    if (chat.id !== activeConversationId) {
      loadSession({ conversationId: chat.id, exchanges: chat.exchanges })
    }
    navigate('/')
    onClose()
  }

  const runSaved = (id: string, query: string) => {
    markSavedUsed(id)
    void run(query)
    navigate('/')
    onClose()
  }

  const handleNewSession = () => {
    newSession()
    navigate('/')
    onClose()
  }

  return (
    <>
      {open && (
        <div
          role="presentation"
          onClick={onClose}
          className="animate-fade fixed inset-0 z-30 bg-black/50 lg:hidden"
        />
      )}

      <div
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex',
          'transition-transform duration-200 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <IconRail
          onNavigate={onClose}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />

        <aside
          className={cn(
            'flex flex-col overflow-hidden bg-surface transition-[width] duration-200',
            collapsed ? 'w-0' : 'w-64 border-r border-line',
          )}
        >
          <div className="flex h-14 w-64 shrink-0 items-center gap-2 px-3.5">
            <span className="text-sm font-semibold text-fg">Chat</span>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              title="Hide sidebar"
              aria-label="Hide sidebar"
              className="ml-auto hidden h-7 w-7 items-center justify-center rounded-full text-subtle transition-colors hover:bg-hover hover:text-fg lg:flex"
            >
              <SidebarMinimalisticIcon size={15} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close navigation"
              className="ml-auto rounded-full p-1 text-subtle hover:bg-hover hover:text-fg lg:hidden"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <div className="w-64 shrink-0 px-3.5 pb-3">
            <button
              type="button"
              onClick={handleNewSession}
              title="Start a new conversation"
              className="mb-2 flex h-8 w-full items-center justify-center gap-1.5 rounded-full bg-accent px-3 text-xs font-medium text-accent-fg shadow-sm transition-colors hover:bg-accent-hover"
            >
              <AddIcon size={14} strokeWidth={2.25} />
              New Chat
            </button>

            <div className="relative">
              <MagnifierIcon
                size={13.5}
                strokeWidth={1.75}
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-subtle"
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search chats…"
                aria-label="Search chats"
                className={cn(
                  'h-9 w-full rounded-full border-none bg-sunken pr-3 pl-8.5',
                  'text-xs text-fg placeholder:text-subtle outline-none transition-colors',
                  'focus:ring-2 focus:ring-accent/15',
                )}
              />
            </div>
          </div>

          <div className="scrollbar-thin min-h-0 w-64 flex-1 shrink-0 overflow-y-auto px-3.5 pb-3">
            {pinnedChats.length > 0 && (
              <div className="mb-3.5">
                <div className="px-1 pb-1 text-[10px] font-medium tracking-wider text-subtle uppercase">
                  Pinned
                </div>
                <div className="space-y-0.5">
                  {pinnedChats.map((entry) => (
                    <div
                      key={entry.chat.id}
                      className="group flex items-center gap-1 rounded-lg py-0.5 pr-0.5 pl-1 transition-colors hover:bg-hover"
                    >
                      <button
                        type="button"
                        title={entry.title}
                        onClick={() => openChat(entry.chat)}
                        className="flex min-w-0 flex-1 items-center py-1 text-left"
                      >
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted group-hover:text-fg">
                          {entry.title}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePin(entry.chat.id)}
                        title="Remove bookmark"
                        aria-label="Remove bookmark"
                        className="shrink-0 rounded-md p-1 text-accent transition-colors hover:text-accent-hover"
                      >
                        <BookmarkFilledIcon size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {visibleSaved.length > 0 && (
              <div className="mb-3.5">
                <div className="px-1 pb-1 text-[10px] font-medium tracking-wider text-subtle uppercase">
                  Saved
                </div>
                <div className="space-y-0.5">
                  {visibleSaved.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      title={item.query}
                      onClick={() => runSaved(item.id, item.query)}
                      className="group flex w-full items-center gap-2.5 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-hover"
                    >
                      <span
                        className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold',
                          SAVED_BADGE_TONES[index % SAVED_BADGE_TONES.length],
                        )}
                      >
                        {item.label.trim().charAt(0).toUpperCase() || '?'}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted group-hover:text-fg">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
                {savedItems.length > visibleSaved.length && (
                  <NavLink
                    to="/saved"
                    onClick={onClose}
                    className="mt-1 inline-block px-1 text-[11px] text-muted underline-offset-4 hover:text-fg hover:underline"
                  >
                    View all {savedItems.length}
                  </NavLink>
                )}
              </div>
            )}

            {groups.length === 0 ? (
              <p className="px-1 py-2 text-[11px] leading-relaxed text-subtle">
                {search ? 'No matching chats.' : 'Your chats will show up here.'}
              </p>
            ) : (
              <div className="space-y-3.5">
                {groups.map((group) => (
                  <div key={group.label}>
                    <div className="px-1 pb-1 text-[10px] font-medium tracking-wider text-subtle uppercase">
                      {group.label}
                    </div>
                    <div className="space-y-0.5">
                      {group.entries.map((entry) => {
                        const active = entry.chat.id === activeConversationId
                        return (
                          <div
                            key={entry.chat.id}
                            className={cn(
                              'group flex items-center gap-1 rounded-lg py-0.5 pr-0.5 pl-1 transition-colors',
                              active ? 'bg-sunken' : 'hover:bg-hover',
                            )}
                          >
                            <button
                              type="button"
                              title={entry.title}
                              onClick={() => openChat(entry.chat)}
                              className="flex min-w-0 flex-1 items-center py-1 text-left"
                            >
                              <span
                                className={cn(
                                  'min-w-0 flex-1 truncate text-[12.5px]',
                                  active ? 'text-fg' : 'text-muted group-hover:text-fg',
                                )}
                              >
                                {entry.title}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => togglePin(entry.chat.id)}
                              title="Bookmark this chat"
                              aria-label="Bookmark this chat"
                              className="shrink-0 rounded-md p-1 text-subtle opacity-0 transition-opacity group-hover:opacity-100 hover:text-fg"
                            >
                              <BookmarkIcon size={12} strokeWidth={1.75} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </>
  )
}
